import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useEntitySearch } from "../hooks/useEntitySearch";
import {
  isLikelyExplorerMergeCandidate,
  sortExplorerMergeCandidates,
} from "../v2/pages/explorerMergeUtils";
import { formatItemApprovalLabel } from "../utils/enrichmentJobCard";

function formatEntityTypeLabel(entityType) {
  return String(entityType || "entity")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function approvalBadgeClass(isInlineLight, approvalStatus) {
  const approval = String(approvalStatus || "Approved").trim();
  if (approval === "Proposed") {
    return isInlineLight
      ? "inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900"
      : "inline-flex rounded-full bg-amber-900/50 px-2 py-0.5 text-[10px] font-semibold text-amber-100";
  }
  return isInlineLight
    ? "inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-900"
    : "inline-flex rounded-full bg-emerald-900/50 px-2 py-0.5 text-[10px] font-semibold text-emerald-100";
}

export default function EnrichmentEntityPickerModal({
  isOpen,
  isInlineLight = false,
  title = "Link to existing item",
  description = "",
  confirmLabel = "Use selected item",
  entityType,
  priorityEntity = null,
  excludeEntityIds = [],
  onClose,
  onSelect,
  anchorName = "",
  relationshipHint = "",
  surfaceName = "",
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    results,
    loading,
    error,
    hasMore,
    total,
    search,
    loadMore,
    initialLoad,
    reset,
  } = useEntitySearch(entityType, {
    enabled: isOpen && Boolean(entityType),
    debounceMs: 250,
    pageSize: priorityEntity ? 100 : 25,
    includeUnapproved: true,
    architectureExplorer: false,
  });

  useEffect(() => {
    if (!isOpen || !entityType) {
      setSearchTerm("");
      setSelectedEntity(null);
      setShowConfirmation(false);
      reset();
      return;
    }
    setSearchTerm("");
    setSelectedEntity(null);
    setShowConfirmation(false);
  }, [entityType, isOpen, reset]);

  useEffect(() => {
    if (!isOpen || !entityType) return;
    if (!searchTerm) {
      initialLoad();
      return;
    }
    search(searchTerm, true);
  }, [entityType, initialLoad, isOpen, search, searchTerm]);

  const visibleResults = useMemo(
    () =>
      (Array.isArray(results) ? results : []).filter(
        (entity) =>
          String(entity?.approval_status || "Approved").trim() !== "Rejected" &&
          !excludeEntityIds.includes(String(entity?.id || ""))
      ),
    [excludeEntityIds, results]
  );

  const rankedResults = useMemo(
    () => sortExplorerMergeCandidates(visibleResults, priorityEntity),
    [priorityEntity, visibleResults]
  );

  if (!isOpen) return null;

  const backdropClass =
    "fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4";
  const shellClass = isInlineLight
    ? "flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
    : "flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-600 bg-slate-900 shadow-2xl";
  const inputClass = isInlineLight
    ? "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
    : "w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-100";

  return createPortal(
    <div className={backdropClass} role="dialog" aria-modal="true" aria-labelledby="enrichment-picker-title">
      <div className={shellClass}>
        <div className={isInlineLight ? "border-b border-slate-200 px-5 py-4" : "border-b border-slate-700 px-5 py-4"}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2
                id="enrichment-picker-title"
                className={isInlineLight ? "text-lg font-semibold text-slate-900" : "text-lg font-semibold text-slate-100"}
              >
                {title}
              </h2>
              <p className={isInlineLight ? "mt-1 text-sm text-slate-600" : "mt-1 text-sm text-slate-400"}>
                {description ||
                  `Search approved and proposed ${formatEntityTypeLabel(entityType)} entities and choose the correct catalog record.`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={isInlineLight ? "text-sm text-slate-500 hover:text-slate-900" : "text-sm text-slate-400 hover:text-slate-100"}
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 space-y-4 overflow-y-auto px-5 py-4">
          <div className="space-y-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${formatEntityTypeLabel(entityType)} entities...`}
              className={inputClass}
            />
            <p className={isInlineLight ? "text-xs text-slate-500" : "text-xs text-slate-400"}>
              {loading ? "Searching catalog..." : `${total || rankedResults.length} result${(total || rankedResults.length) === 1 ? "" : "s"}`}
            </p>
            {priorityEntity?.id ? (
              <p className={isInlineLight ? "text-xs text-indigo-600" : "text-xs text-indigo-300"}>
                Likely duplicate candidates are ranked first using the source entity's name,
                aliases, and description.
              </p>
            ) : null}
          </div>

          <div
            className={
              isInlineLight
                ? "max-h-[16rem] space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2 md:max-h-[18rem]"
                : "max-h-[16rem] space-y-2 overflow-y-auto rounded-xl border border-slate-700 bg-slate-950/40 p-2 md:max-h-[18rem]"
            }
          >
            {!loading && rankedResults.length === 0 ? (
              <div className={isInlineLight ? "px-3 py-8 text-center text-sm text-slate-500" : "px-3 py-8 text-center text-sm text-slate-400"}>
                No on-map or pending items matched this search.
              </div>
            ) : null}

            {rankedResults.map((entity) => {
              const selected = String(selectedEntity?.id || "") === String(entity?.id || "");
              const approval = String(entity?.approval_status || "Approved").trim() || "Approved";
              const showLikelyDuplicate =
                Boolean(priorityEntity?.id) &&
                isLikelyExplorerMergeCandidate(priorityEntity, entity);
              return (
                <button
                  key={entity.id}
                  type="button"
                  onClick={() => setSelectedEntity(entity)}
                  className={
                    selected
                      ? isInlineLight
                        ? "w-full rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-3 text-left"
                        : "w-full rounded-xl border border-indigo-500/50 bg-indigo-950/35 px-3 py-3 text-left"
                      : isInlineLight
                        ? "w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-left hover:border-slate-300"
                        : "w-full rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-3 text-left hover:border-slate-500"
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={isInlineLight ? "text-sm font-medium text-slate-900" : "text-sm font-medium text-slate-100"}>
                        {entity.name}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className={approvalBadgeClass(isInlineLight, approval)}>
                          {formatItemApprovalLabel(approval)}
                        </span>
                        {showLikelyDuplicate ? (
                          <span
                            className={
                              isInlineLight
                                ? "inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-800"
                                : "inline-flex rounded-full bg-indigo-900/50 px-2 py-0.5 text-[10px] font-semibold text-indigo-200"
                            }
                          >
                            Likely duplicate
                          </span>
                        ) : null}
                        <span className={isInlineLight ? "text-[11px] text-slate-500" : "text-[11px] text-slate-400"}>
                          {formatEntityTypeLabel(entity.entity_type || entity.type || entityType)}
                        </span>
                      </div>
                      {entity.description ? (
                        <p className={isInlineLight ? "mt-2 text-xs text-slate-600" : "mt-2 text-xs text-slate-400"}>
                          {entity.description}
                        </p>
                      ) : null}
                    </div>
                    {selected ? (
                      <span className={isInlineLight ? "text-xs font-medium text-indigo-700" : "text-xs font-medium text-indigo-300"}>
                        Selected
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          {hasMore ? (
            <button
              type="button"
              onClick={loadMore}
              disabled={loading}
              className={
                isInlineLight
                  ? "rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
                  : "rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-200"
              }
            >
              {loading ? "Loading..." : "Load more"}
            </button>
          ) : null}

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        {showConfirmation && selectedEntity ? (
          <div className={isInlineLight ? "border-t border-slate-200 px-5 py-4 space-y-3" : "border-t border-slate-700 px-5 py-4 space-y-3"}>
            <div
              className={
                isInlineLight
                  ? "rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
                  : "rounded-lg border border-emerald-600/40 bg-emerald-950/30 px-4 py-3"
              }
            >
              <p
                className={
                  isInlineLight
                    ? "text-sm font-medium text-emerald-900"
                    : "text-sm font-medium text-emerald-100"
                }
              >
                Confirm entity linkage
              </p>
              <p
                className={
                  isInlineLight
                    ? "mt-1.5 text-sm text-emerald-800 leading-relaxed"
                    : "mt-1.5 text-sm text-emerald-200 leading-relaxed"
                }
              >
                {relationshipHint ? (
                  <>
                    A <span className="font-semibold">{relationshipHint}</span> relationship will be
                    established between{" "}
                    <span className="font-semibold">{anchorName || "the current entity"}</span> and{" "}
                    <span className="font-semibold">{selectedEntity.name}</span>.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">{surfaceName || "The proposed entity"}</span>{" "}
                    will be linked to the existing catalog record{" "}
                    <span className="font-semibold">{selectedEntity.name}</span>
                    {anchorName ? (
                      <>
                        {" "}and any proposed relationships involving this entity will connect to{" "}
                        <span className="font-semibold">{anchorName}</span>.
                      </>
                    ) : (
                      <>. Any proposed relationships involving this entity will use the selected record.</>
                    )}
                  </>
                )}
              </p>
              <p
                className={
                  isInlineLight
                    ? "mt-1.5 text-xs text-emerald-700"
                    : "mt-1.5 text-xs text-emerald-300"
                }
              >
                The name "{surfaceName || selectedEntity.name}" will be added as an alias to the
                selected entity if not already present.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className={
                  isInlineLight
                    ? "rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
                    : "rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200"
                }
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => {
                  onSelect?.(selectedEntity);
                  setShowConfirmation(false);
                }}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Confirm linkage
              </button>
            </div>
          </div>
        ) : (
          <div className={isInlineLight ? "flex justify-end gap-2 border-t border-slate-200 px-5 py-4" : "flex justify-end gap-2 border-t border-slate-700 px-5 py-4"}>
            <button
              type="button"
              onClick={onClose}
              className={
                isInlineLight
                  ? "rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
                  : "rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200"
              }
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedEntity}
              onClick={() => {
                if (!selectedEntity) return;
                setShowConfirmation(true);
              }}
              className={
                isInlineLight
                  ? "rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                  : "rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              }
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
